import { isTelegramMiniApp } from '../infrastructure/auth/isTelegramMiniApp';
import { getFromApi } from '../infrastructure/requests/request-helpers';

export const INVITED_BY_USER_ID_STORAGE_KEY = 'lingvo-news-invited-by-user-id';

function getStorage(): Storage | null {
	if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
		return null;
	}
	return window.localStorage;
}

export interface InviteLinksResponse {
	success: boolean;
	webInviteLink: string;
	telegramInviteLink: string | null;
}

function normalizeStoredValue(value: string | null): string | null {
	if (!value) {
		return null;
	}
	try {
		const parsed = JSON.parse(value) as unknown;
		return typeof parsed === 'string' && parsed.trim().length > 0 ? parsed.trim() : null;
	} catch {
		return value.trim().length > 0 ? value.trim() : null;
	}
}

export function readStoredInvitedByUserId(): string | null {
	return normalizeStoredValue(getStorage()?.getItem(INVITED_BY_USER_ID_STORAGE_KEY) ?? null);
}

export function storeInvitedByUserId(invitedByUserId: string): void {
	const storage = getStorage();
	const normalized = invitedByUserId.trim();
	if (!storage || normalized.length===0) {
		return;
	}
	storage.setItem(INVITED_BY_USER_ID_STORAGE_KEY, JSON.stringify(normalized));
}

export function clearStoredInvitedByUserId(): void {
	getStorage()?.removeItem(INVITED_BY_USER_ID_STORAGE_KEY);
}

export function captureInvitedByUserId(value: unknown): string | null {
	const normalized = typeof value === 'string' ? value.trim() : '';
	if (!normalized) {
		return null;
	}
	storeInvitedByUserId(normalized);
	return normalized;
}

export function createInviteLink(userId: string): string {
	const origin = typeof window !== 'undefined' && window.location.origin
		? window.location.origin
		: 'https://lingvo.news';
	return `${origin}?invitedByUserId=${encodeURIComponent(userId)}`;
}

export function isTelegramBotInviteLink(inviteLink: string): boolean {
	return /^https:\/\/t\.me\/[^/?]+(?:\?|$)/i.test(inviteLink);
}

export async function getInviteLinksFromApi(): Promise<InviteLinksResponse> {
	return await getFromApi<InviteLinksResponse>('/api/auth/invite-link');
}

export function createInviteShareText(inviteLink: string): string {
	return `Or invite friends and you both get 1 month for free.\n${inviteLink}`;
}

export function createTelegramShareUrl(inviteLink: string): string {
	const text = 'Join LingvoNews with my invite link and help me unlock +1 month of Premium.';
	return `https://t.me/share/url?url=${encodeURIComponent(inviteLink)}&text=${encodeURIComponent(text)}`;
}

export async function shareInviteLink(inviteLink: string): Promise<'shared' | 'copied'> {
	const telegram = (window as typeof window & {
		Telegram?: { WebApp?: { openTelegramLink?: (url: string) => void } };
	}).Telegram;

	if (isTelegramMiniApp() && telegram?.WebApp?.openTelegramLink) {
		telegram.WebApp.openTelegramLink(createTelegramShareUrl(inviteLink));
		return 'shared';
	}

	if (navigator.share) {
		await navigator.share({
			text: createInviteShareText(inviteLink),
			url: inviteLink
		});
		return 'shared';
	}

	if (navigator.clipboard?.writeText) {
		await navigator.clipboard.writeText(inviteLink);
		return 'copied';
	}

	window.open(createTelegramShareUrl(inviteLink), '_blank', 'noopener,noreferrer');
	return 'shared';
}
